import type { StateCreator } from "zustand";
import type {
  InterviewDeletionData,
  InterviewDeletionState,
  InterviewFormData,
  InterviewGenerationState,
} from "@/app/[lng]/interviewer/_lib/types";
import type { ApiResponse } from "@/repositories/common-types";
import type {
  GenerateInterviewAnswersResponse,
  GenerateInterviewResponse,
} from "@/repositories/interviewer/types";

interface InterviewerSliceState {
  generationState: InterviewGenerationState;
  deletionState: InterviewDeletionState;
  interviewerFormData: InterviewFormData | null;
  deletionData: InterviewDeletionData | null;
  generatedInterviewData: ApiResponse<GenerateInterviewResponse> | null;
  generatedAnswersData: ApiResponse<GenerateInterviewAnswersResponse> | null;
}

interface InterviewerSliceActions {
  startGeneration: () => void;
  showForm: (data?: InterviewFormData) => void;
  showConfirm: (data: InterviewFormData) => void;
  startGeneratingInterview: () => void;
  handleInterviewGenerationSuccess: (data: ApiResponse<GenerateInterviewResponse>) => void;
  startGeneratingAnswers: () => void;
  handleAnswersGenerationSuccess: (data: ApiResponse<GenerateInterviewAnswersResponse>) => void;
  handleGenerationError: () => void;
  resetGeneration: () => void;
  cancelGeneration: () => void;
  showDeletionConfirm: (data: InterviewDeletionData) => void;
  resetDeletion: () => void;
  cancelDeletion: () => void;
}

export type InterviewerSlice = InterviewerSliceState & InterviewerSliceActions;

export const createInterviewerSlice: StateCreator<
  InterviewerSlice,
  [["zustand/devtools", never]],
  [],
  InterviewerSlice
> = (set) => ({
  generationState: "idle",
  deletionState: "idle",
  interviewerFormData: null,
  deletionData: null,
  generatedInterviewData: null,
  generatedAnswersData: null,

  startGeneration: () => set({ generationState: "form" }),

  showForm: (data) => set({ generationState: "form", interviewerFormData: data || null }),

  showConfirm: (data) => set({ generationState: "confirm", interviewerFormData: data }),

  startGeneratingInterview: () => set({ generationState: "generating-interview" }),

  handleInterviewGenerationSuccess: (data) =>
    set({
      generationState: "generating-answers",
      generatedInterviewData: data,
    }),

  startGeneratingAnswers: () => set({ generationState: "generating-answers" }),

  handleAnswersGenerationSuccess: (data) =>
    set({
      generationState: "idle",
      interviewerFormData: null,
      generatedAnswersData: data,
      generatedInterviewData: null,
    }),

  handleGenerationError: () => set({ generationState: "form" }),

  resetGeneration: () =>
    set({
      generationState: "idle",
      interviewerFormData: null,
      generatedInterviewData: null,
      generatedAnswersData: null,
    }),

  cancelGeneration: () =>
    set({
      generationState: "idle",
      interviewerFormData: null,
      generatedInterviewData: null,
      generatedAnswersData: null,
    }),

  showDeletionConfirm: (data) =>
    set({
      deletionState: "confirming",
      deletionData: data,
    }),

  resetDeletion: () =>
    set({
      deletionState: "idle",
      deletionData: null,
    }),

  cancelDeletion: () =>
    set({
      deletionState: "idle",
      deletionData: null,
    }),
});


